import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, X, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";

const METHODS = [
  { key: "gift_card", label: "Gift Card" },
  { key: "promotional", label: "Promotional" },
  { key: "complimentary", label: "Complimentary" },
  { key: "trade_barter", label: "Trade/Barter" },
  { key: "other", label: "Other" },
];

function GiftCodeCheck({ code, setCode, card, setCard }) {
  const [busy, setBusy] = useState(false);
  const check = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/gift-cards/lookup/${encodeURIComponent(code.trim())}`);
      setCard(data);
    } catch (e) { setCard({ error: formatApiError(e.response?.data?.detail) }); }
    finally { setBusy(false); }
  };
  const usable = card && !card.error && ["active", "partially_used"].includes(card.status);
  return (
    <div>
      <label className="text-xs text-[#4A5D6B]">Gift card code</label>
      <div className="flex gap-2 mt-1">
        <input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setCard(null); }} placeholder="EYW-XXXX-XXXX-XXXX" data-testid="mp-gift-code" className="eyw-input flex-1 uppercase font-mono" />
        <button type="button" onClick={check} disabled={!code.trim() || busy} data-testid="mp-gift-check" className="eyw-btn-ghost text-sm">{busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Check</button>
      </div>
      {card?.error && <p className="text-xs text-red-600 mt-1.5" data-testid="mp-gift-error">{card.error}</p>}
      {card && !card.error && (
        <p className={`text-xs mt-1.5 ${usable ? "text-[#5C8A72]" : "text-red-600"}`} data-testid="mp-gift-status">
          {usable ? `Valid · ${money(card.balance)} available of ${money(card.value)}` : card.status === "voided" ? "This gift card has been voided and can't be used." : "This gift card has already been fully redeemed."}
        </p>
      )}
    </div>
  );
}

// Record a manual (non-Stripe) payment against a booking or event registration.
export default function RecordPaymentModal({ kind, refId, onClose }) {
  const qc = useQueryClient();
  const { data: t, isLoading, refetch } = useQuery({ queryKey: ["payment-target", kind, refId], queryFn: () => api.get(`/admin/payments/record/${kind}/${refId}`).then((r) => r.data) });
  const [method, setMethod] = useState("gift_card");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [code, setCode] = useState("");
  const [card, setCard] = useState(null);
  const [settle, setSettle] = useState(false);
  const [busy, setBusy] = useState(false);
  const due = t?.balance_due || 0;
  const giftApply = card && !card.error ? Math.min(card.balance, due, Number(amount) || card.balance) : 0;
  const giftOk = method !== "gift_card" || (card && !card.error && ["active", "partially_used"].includes(card.status));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payments/manual", {
        kind, ref_id: refId, method, note, settle_remaining: settle,
        amount: amount === "" ? null : Number(amount), gift_card_code: method === "gift_card" ? code : "",
      });
      const left = data.booking.balance_due;
      toast.success(left > 0 ? `${money(data.payment.amount)} recorded — ${money(left)} still due` : `${money(data.payment.amount)} recorded — balance settled`);
      qc.invalidateQueries({ queryKey: ["admin-outstanding"] });
      qc.invalidateQueries({ queryKey: ["admin-gift-cards"] });
      qc.invalidateQueries({ queryKey: ["admin-manual-payments"] });
      setAmount(""); setNote(""); setCode(""); setCard(null); setSettle(false);
      if (left <= 0) onClose(); else refetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm grid place-items-center px-4" data-testid="record-payment-modal">
      <div className="w-full max-w-lg bg-[#F9F7F3] rounded-3xl shadow-2xl p-7 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <p className="eyw-label">Record a manual payment</p>
            <h3 className="font-serif text-xl text-[#1A242B]">{t?.title || "Booking"}</h3>
            {t && <p className="text-xs text-[#4A5D6B]">{t.customer_name || t.customer_email} · {fmtDate(t.date)}</p>}
          </div>
          <button onClick={onClose} data-testid="mp-close" className="text-[#4A5D6B]"><X size={18} /></button>
        </div>
        {isLoading ? <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" /></div> : (
          <>
            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              <div className="eyw-card p-3"><p className="text-[10px] uppercase text-[#8FA89B]">Total</p><p className="font-serif text-lg">{money(t.total_price)}</p></div>
              <div className="eyw-card p-3"><p className="text-[10px] uppercase text-[#8FA89B]">Paid</p><p className="font-serif text-lg text-[#5C8A72]">{money(t.amount_paid)}</p></div>
              <div className="eyw-card p-3"><p className="text-[10px] uppercase text-[#8FA89B]">Due</p><p className="font-serif text-lg text-[#B5794E]" data-testid="mp-due">{money(due)}</p></div>
            </div>
            {(t.manual_payments || []).length > 0 && (
              <ul className="mt-3 text-xs text-[#4A5D6B] space-y-1" data-testid="mp-lines">
                {t.manual_payments.map((l, i) => <li key={i}>• {l.method_label} {money(l.amount)}{l.gift_card_code ? ` (${l.gift_card_code})` : ""}{l.note ? ` — ${l.note}` : ""}</li>)}
              </ul>
            )}
            {due <= 0 ? (
              <p className="mt-5 text-sm text-[#5C8A72] flex items-center gap-2" data-testid="mp-paid"><CheckCircle2 size={16} /> This booking is fully paid.</p>
            ) : (
              <div className="mt-5 space-y-3">
                <div>
                  <label className="text-xs text-[#4A5D6B]">Payment method</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {METHODS.map((m) => (
                      <button key={m.key} type="button" onClick={() => setMethod(m.key)} data-testid={`mp-method-${m.key}`}
                        className={`text-xs rounded-full px-3 py-1.5 border ${method === m.key ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#144F75]/25 text-[#144F75]"}`}>{m.label}</button>
                    ))}
                  </div>
                </div>
                {method === "gift_card" && <GiftCodeCheck code={code} setCode={setCode} card={card} setCard={setCard} />}
                <div>
                  <label className="text-xs text-[#4A5D6B]">{method === "gift_card" ? "Amount to apply (leave blank to use the most available)" : "Amount applied"}</label>
                  <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="mp-amount" className="eyw-input w-full" placeholder={method === "gift_card" && giftApply ? giftApply.toFixed(2) : due.toFixed(2)} />
                </div>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) — e.g. promo name or trade details" data-testid="mp-note" className="eyw-input w-full" />
                <label className="flex items-start gap-2 text-xs text-[#4A5D6B]">
                  <input type="checkbox" checked={settle} onChange={(e) => setSettle(e.target.checked)} data-testid="mp-settle" className="mt-0.5" />
                  Mark the balance as settled even if this doesn't cover the full amount
                </label>
                <button onClick={submit} disabled={busy || !giftOk || (method !== "gift_card" && !amount)} data-testid="mp-submit" className="eyw-btn w-full disabled:opacity-50">
                  {busy && <Loader2 size={16} className="animate-spin" />} Record payment
                </button>
                <p className="text-[11px] text-[#8FA89B]">Payment records are permanent once saved. Any remaining balance stays payable by card or another method.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
